<template>
  <div class="selector__wrapper">
    <div
      v-if="open"
      class="selector__overlay"
      @click="closeItems(true)" />
    <div
      ref="select"
      :class="{ 'selector--active': open }"
      class="selector">
      <div
        class="selector__value"
        @click="toggleItems">
        <slot
          :select-item="selectItem"
          :selected-items="selectedItems"
          :open-items="openItems"
          name="value" />
        <slot name="arrow" />
      </div>
      <div
        v-show="open"
        ref="items"
        class="selector__options"
        :class="{ 'selector__options--open': open }">
        <slot
          :selected-item-ids="selectedItemIds"
          :selected-indexes="selectedIndexes"
          :available-items="availableItems"
          :select-item="selectItem"
          :close-items="closeItems"
          name="options" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseSelect',

  props: {
    items: {
      type: Array,
      required: true,
    },

    // value must be an Array if multiple = true
    value: {
      type: [String, Number, Object, Array],
      default: null,
    },

    valueField: {
      type: String,
      default: null,
    },

    // checks if should hide selected value if selector not multiple
    shouldHideSelectedValue: {
      type: Boolean,
      default: true,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    // Разрешает устанавливать значени при инициализации
    setInitValue: {
      type: Boolean,
      default: false,
    },

    // Разрешает устанавливать значени при инициализации
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      open: false,
      selectedIndexes: [],
    };
  },

  computed: {
    selectedItemIds() {
      if (!this.multiple) {
        return null;
      }

      if (!this.value || !this.value.length) {
        return [];
      }

      return this.value.map((value) => {
        if (typeof value === 'object') {
          return value.id;
        }

        return value;
      });
    },

    availableItems() {
      if (!this.value || this.multiple) {
        return this.items;
      }

      if (this.shouldHideSelectedValue) {
        return this.items.filter((item) => {
          if (typeof item === 'object') {
            return +item.id !== +this.value.id;
          }

          return item !== this.value;
        });
      }

      return this.items;
    },

    selectedItems() {
      return this.items.filter((item, index) => this.selectedIndexes[index]);
    },
  },

  created() {
    this.selectedIndexes = (new Array(this.items.length)).fill(false);

    if (this.value) {
      if (this.multiple) {
        this.value.forEach((val) => {
          const itemIndex = this.items.findIndex((item) => {
            if (typeof val === 'object') {
              return this.valueField
                ? val[this.valueField] === item[this.valueField]
                : JSON.stringify(val) === JSON.stringify(item);
            }
            return val === item;
          });
          this.selectedIndexes[itemIndex] = true;
        });
      } else {
        const itemIndex = this.items.findIndex((item) => {
          if (typeof val === 'object') {
            return this.valueField
              ? this.value[this.valueField] === item[this.valueField]
              : JSON.stringify(this.value) === JSON.stringify(item);
          }
          return this.value === item;
        });
        this.selectedIndexes[itemIndex] = true;
      }
    } else if (this.setInitValue) {
      if (this.multiple) {
        this.emitValue([]);
      } else {
        this.emitValue(this.items[0]);
      }
    }
  },

  methods: {
    emitValue(val) {
      if (this.multiple) {
        const result = [];
        this.selectedIndexes.forEach((isSelected, index) => {
          if (!isSelected) {
            return false;
          }

          const _val = this.items[index];
          return result.push(this.valueField && typeof _val === 'object' ? _val[this.valueField] : _val);
        });
        this.$emit('input', result);
      } else {
        this.selectedIndexes = Array(this.items.length).fill(false);
        const itemIndex = this.items.findIndex((item) => {
          if (typeof val === 'object') {
            return this.valueField
              ? val[this.valueField] === item[this.valueField]
              : JSON.stringify(val) === JSON.stringify(item);
          }
          return val === item;
        });
        this.selectedIndexes[itemIndex] = true;
        this.$emit('input', this.valueField && typeof val === 'object' ? val[this.valueField] : val);
      }
    },

    toggleItems() {
      if (this.open) {
        this.closeItems();
      } else {
        this.openItems();
      }
    },

    openItems() {
      if (this.disabled) {
        return;
      }

      this.open = true;
      const selectRect = this.$refs.select.getBoundingClientRect();
      const optionsRect = this.$refs.items.getBoundingClientRect();
      const isOverflowed = selectRect.bottom + optionsRect.height > window.innerHeight;

      if (isOverflowed) {
        this.$refs.items.style.position = 'absolute';
        this.$refs.items.style.top = `-${selectRect.height + optionsRect.height - 1}px`;
      } else {
        this.$refs.items.style.position = 'static';
        this.$refs.items.style.top = '0';
      }

      this.$emit('open');
    },

    closeItems(withAutoSelect = false) {
      this.open = false;
      this.$emit('close', withAutoSelect);
    },

    selectItem(item) {
      if (this.multiple) {
        const itemIndex = this.items.findIndex((selectedValue) => {
          if (typeof selectedValue === 'object') {
            if (this.valueField) {
              return item[this.valueField] === selectedValue[this.valueField];
            }
            return JSON.stringify(item) === JSON.stringify(selectedValue);
          }

          return item === selectedValue;
        });

        // переключаем состояние выбран элемент или нет
        this.$set(this.selectedIndexes, itemIndex, !this.selectedIndexes[itemIndex]);
        this.emitValue();
      } else {
        this.emitValue(item);
        this.closeItems();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .selector__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 17;
  }

  .selector__wrapper {
    font-family: Rubik, sans-serif;
  }

  .selector {
    position: relative;
    z-index: 16;

    &--active {
      z-index: 17;
    }
  }

  .selector__value {
    position: relative;
    text-align: center;
    cursor: pointer;
    z-index: 2;
    transition: background-color 0.15s ease-out;
  }

  .selector__options {
    left: 0;
    z-index: 1;
    height: 0;
    position: relative;
    top: 0;
    transition: background-color, opacity .15s ease-out;
    overflow: hidden;
    padding: 0;
    margin: 0;
    visibility: hidden;
    opacity: 0;

    &--open {
      opacity: 1;
      visibility: visible;
      overflow: visible;
    }
  }

  .selector__option--active {
    color: $color--basic-light-gray;
  }
</style>
